import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';

import {
  getInvoiceDetails,
  getServicePlanInvoiceDetails,
} from 'api';

const useInvoiceDetails = ({ invoiceId, portalId }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const stripeRedirectResult = searchParams.get('stripe_result');
  const isServicePlanInvoice = searchParams.get('type') === 'service-plan';

  const clearStripeResultParam = useCallback(() => {
    if (!searchParams.get('stripe_result')) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('stripe_result');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let invalidatorTimeout;

    if (stripeRedirectResult === 'success') {
      invalidatorTimeout = setTimeout(() => {
        queryClient.invalidateQueries(`invoice-${invoiceId}`);
        queryClient.invalidateQueries(`service-plan-invoice-${invoiceId}`);
        clearStripeResultParam();
      }, 3000);
    }

    if (stripeRedirectResult === 'cancel') {
      clearStripeResultParam();
    }

    return () => {
      clearInterval(invalidatorTimeout);
    };
  }, [stripeRedirectResult, invoiceId, queryClient, clearStripeResultParam]);

  const {
    isLoading: isJobInvoiceLoading,
    data: jobInvoiceResponse,
    isError: isJobInvoiceError,
    isFetched: isJobInvoiceFetched,
  } = useQuery(
    [`invoice-${invoiceId}`, portalId, invoiceId],
    getInvoiceDetails,
    {
      enabled: !!portalId && !isServicePlanInvoice,
    },
  );

  const {
    isLoading: isServicePlanInvoiceLoading,
    data: servicePlanInvoiceResponse,
    isError: isServicePlanInvoiceError,
    isFetched: isServicePlanInvoiceFetched,
  } = useQuery(
    [`service-plan-invoice-${invoiceId}`, portalId, invoiceId],
    getServicePlanInvoiceDetails,
    {
      enabled: !!portalId && (isServicePlanInvoice || isJobInvoiceError),
    },
  );

  const invoiceResponse = isServicePlanInvoice
    ? servicePlanInvoiceResponse
    : (jobInvoiceResponse || servicePlanInvoiceResponse);

  const isLoading = isServicePlanInvoice
    ? isServicePlanInvoiceLoading
    : (isJobInvoiceLoading || (isJobInvoiceError && isServicePlanInvoiceLoading));

  const isFetched = isServicePlanInvoice
    ? isServicePlanInvoiceFetched
    : (isJobInvoiceFetched || isServicePlanInvoiceFetched);

  let isError = false;

  if (!isLoading && !invoiceResponse) {
    isError = isServicePlanInvoice ? isServicePlanInvoiceError : isJobInvoiceError;
  }

  return {
    invoice: invoiceResponse?.data,
    isServicePlanInvoice,
    isLoading,
    isFetched,
    isError,
  };
};

export default useInvoiceDetails;
